import React from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { selectSettings } from "../../store/slices/settingsSlice";
import { getTenantId } from "../../api/apiHelpers";

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  ogTitle?: string;
  ogDescription?: string;
  noindex?: boolean;
  children?: React.ReactNode;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  canonicalUrl,
  ogType = "website",
  ogImage,
  ogTitle,
  ogDescription,
  noindex = false,
  children,
}) => {
  const settings: any = useSelector(selectSettings);

  const suffix = settings?.seo_title_suffix || settings?.business_name || "";
  const fullTitle = title ? (suffix ? `${title} | ${suffix}` : title) : suffix;
  const metaDescription =
    description || settings?.seo_default_description || "";
  const socialImage = ogImage || settings?.seo_social_image || "";
  const gaTrackingId = settings?.ga_tracking_id || "";
  // Platform-wide GA4 measurement ID — fires on every tenant alongside
  // the tenant's own gaTrackingId, with tenant_slug + tenant_name
  // attached as event parameters so the platform can roll traffic up
  // across tenants and segment per tenant in any GA4 report. Set via
  // REACT_APP_PLATFORM_GA_MEASUREMENT_ID at build time on Netlify.
  const platformGaId = process.env.REACT_APP_PLATFORM_GA_MEASUREMENT_ID || "";
  const tenantSlug = (() => {
    try {
      return getTenantId() || "";
    } catch {
      return "";
    }
  })();
  const tenantName = settings?.business_name || "";
  const searchConsoleVerification =
    settings?.search_console_verification || "";

  // Pick whichever ID is available to load the shared gtag.js library
  // — once loaded, it can be configured for any number of IDs in
  // parallel, so loading it twice would just duplicate work.
  const primaryGtagId = gaTrackingId || platformGaId;

  return (
    <Helmet>
      {fullTitle && <title>{fullTitle}</title>}
      {metaDescription && (
        <meta name="description" content={metaDescription} />
      )}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {searchConsoleVerification && (
        <meta
          name="google-site-verification"
          content={searchConsoleVerification}
        />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      {(ogTitle || fullTitle) && (
        <meta property="og:title" content={ogTitle || fullTitle} />
      )}
      {(ogDescription || metaDescription) && (
        <meta
          property="og:description"
          content={ogDescription || metaDescription}
        />
      )}
      {socialImage && <meta property="og:image" content={socialImage} />}
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      {(ogTitle || fullTitle) && (
        <meta name="twitter:title" content={ogTitle || fullTitle} />
      )}
      {(ogDescription || metaDescription) && (
        <meta
          name="twitter:description"
          content={ogDescription || metaDescription}
        />
      )}
      {socialImage && <meta name="twitter:image" content={socialImage} />}

      {/* Google Analytics — loads gtag.js once, configures one or both IDs */}
      {primaryGtagId && (
        <script
          async
          src={`https://www.googletagmanager.com/gtag/js?id=${primaryGtagId}`}
        />
      )}
      {primaryGtagId && (
        <script>{`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          ${gaTrackingId ? `gtag('config', '${gaTrackingId}');` : ""}
          ${platformGaId ? `gtag('config', '${platformGaId}', { tenant_slug: '${tenantSlug.replace(/'/g, "\\'")}', tenant_name: '${tenantName.replace(/'/g, "\\'")}' });` : ""}
        `}</script>
      )}

      {children}
    </Helmet>
  );
};

export default SEOHead;
